/**
 * L0 music (67.19): the action clock's fight, read as the conductor's state. The turn game drives the music from
 * its turn phases; a real-time fight has none, so the music follows how hot the fight is around the held person:
 * gunfire heard around them (to tension at most) and danger to them (a near miss, a hit taken, a round landing
 * close, a kill — the combat layer), each cooling on its own clock. Quiet → calm; a battle within earshot →
 * tension; under fire → combat; low health → the heartbeat; tickets or soldiers running out → the endgame. Pure.
 */
import type { ConductorState, ConductorMatchPhase } from './conductor';

/**
 * Two kinds of heat (owner, Oct 10: "we never exit calm in lab mode"; a headless 6-a-side then showed the first cut
 * pinned in combat 96 % of the match — some seventeen shots a second from the whole field saturated it, so the music
 * never moved). **Gunfire around you** (`noise`) raises it to tension and no further — a battle you can hear but are
 * not in; **danger to you** (`heat`: a near miss, a hit taken, a round landing close, your own kill, a little for your
 * own shots) is what brings the combat layer. Each halves on its own clock.
 */
export const HEAT_HALF_LIFE = 4, NOISE_HALF_LIFE = 3;
/** Gunfire you are not in reaches this much: the conductor's tension band (0.1 up), well under its 0.3 combat line — at
 * 0.28 a busy field sat at the cap and a breath of danger tipped it over, so it never left combat. */
export const NOISE_CAP = 0.18;

export class CombatPulse {
  /** danger to the held person */
  heat = 0;
  /** gunfire heard around them */
  noise = 0;
  /** seconds since anything happened near the held person (the conductor fades an idle mix after ten) */
  sinceAction = 0;

  /** What the conductor reads: the danger, plus the gunfire up to the tension band. */
  get intensity(): number { return Math.min(1, this.heat + Math.min(this.noise, NOISE_CAP)); }

  private hot(x: number): void { this.heat = Math.min(1, this.heat + x); this.sinceAction = 0; }

  /** A shot heard `people` away: others' gunfire is noise (nearer counts more); your own a little heat. */
  shot(people: number, own: boolean): void {
    if (own) { this.hot(0.012); return; }
    this.noise = Math.min(1, this.noise + 0.03 / (1 + people / 15));
    if (people < 40) this.sinceAction = 0;
  }
  /** A round passed `missPeople` from the head (within the near-miss range, five people): one past the ear counts, one four people off barely. */
  nearMiss(missPeople = 0): void { const c = Math.max(0, 1 - missPeople / 5); this.hot(0.22 * c * c); }
  /** A round landed `people` away. */
  impactNear(people: number): void { if (people < 6) this.hot(0.07 * (1 - people / 6)); }
  /** The held person was hit. */
  hitTaken(): void { this.hot(0.4); }
  /** The held person killed someone. */
  kill(): void { this.hot(0.15); }

  tick(dt: number): void {
    this.heat *= Math.pow(0.5, dt / HEAT_HALF_LIFE);
    this.noise *= Math.pow(0.5, dt / NOISE_HALF_LIFE);
    if (this.heat < 0.01) this.heat = 0;
    if (this.noise < 0.01) this.noise = 0;
    this.sinceAction += dt;
  }
}

/**
 * The match's phase from the viewer's side: by the tickets when the match has them (either side down to a fifth →
 * endgame, a half → mid), else by the soldiers standing (the turn game's rule, `deriveMatchPhase`'s 1v1 branch).
 */
export function actionMatchPhase(tickets: number[] | null, startTickets: number | null, aliveEnemies: number, startEnemies: number): ConductorMatchPhase {
  if (tickets && startTickets && startTickets > 0) {
    const low = Math.min(...tickets) / startTickets;
    return low <= 0.2 ? 'endgame' : low <= 0.5 ? 'mid' : 'early';
  }
  if (startEnemies > 1 && aliveEnemies <= 1) return 'endgame';
  if (startEnemies > 1 && aliveEnemies <= startEnemies * 0.5) return 'mid';
  return 'early';
}

/** The conductor's state for a real-time fight: shots about → its 'aiming' tension; past 0.3 its combat overlay takes over. */
export function actionConductorState(pulse: CombatPulse, health01: number, phase: ConductorMatchPhase): ConductorState {
  return {
    turnPhase: pulse.intensity >= 0.1 ? 'aiming' : 'idle',
    combatIntensity: pulse.intensity,
    playerHealth: Math.max(0, Math.min(1, health01)),
    buildMode: false,
    matchPhase: phase,
    timeSinceLastAction: pulse.sinceAction,
  };
}
